import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  SelectChangeEvent,
  Button,
  Alert,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import moment from 'moment';
import { ResponsiveLine } from '@nivo/line';
import { formatNumber } from '../../helpers/formatNumber';
import {
  fetchVnrRevenueSummary,
  fetchVnrRevenueTimeline,
  fetchVnrSourceAnalysis,
  fetchVnrNewsletterAnalysis,
  fetchVnrDomainAnalysis,
  VnrRevenueSummaryData,
  VnrRevenueTimelineData,
  VnrSourceAnalysisData,
  VnrNewsletterAnalysisData,
  VnrDomainAnalysisData,
} from '../../helpers/vnrRevenueData';

const VnrRevenue: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Date filters - dual date ranges
  const [leadStartDate, setLeadStartDate] = useState<Date>(moment().subtract(1, 'year').toDate());
  const [leadEndDate, setLeadEndDate] = useState<Date>(new Date());
  const [revenueStartDate, setRevenueStartDate] = useState<Date>(
    moment().subtract(1, 'year').toDate(),
  );
  const [revenueEndDate, setRevenueEndDate] = useState<Date>(new Date());

  // Source filter
  const [selectedSources, setSelectedSources] = useState<string[]>(['nl', 'sam']);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Data state
  const [summary, setSummary] = useState<VnrRevenueSummaryData | null>(null);
  const [timeline, setTimeline] = useState<VnrRevenueTimelineData[]>([]);
  const [sourceAnalysis, setSourceAnalysis] = useState<VnrSourceAnalysisData[]>([]);
  const [newsletterAnalysis, setNewsletterAnalysis] = useState<VnrNewsletterAnalysisData[]>([]);
  const [domainAnalysis, setDomainAnalysis] = useState<VnrDomainAnalysisData[]>([]);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  // Stable date strings for API calls
  const leadStartDateStr = useMemo(
    () => moment(leadStartDate).format('YYYY-MM-DD'),
    [leadStartDate],
  );
  const leadEndDateStr = useMemo(() => moment(leadEndDate).format('YYYY-MM-DD'), [leadEndDate]);
  const revenueStartDateStr = useMemo(
    () => moment(revenueStartDate).format('YYYY-MM-DD'),
    [revenueStartDate],
  );
  const revenueEndDateStr = useMemo(
    () => moment(revenueEndDate).format('YYYY-MM-DD'),
    [revenueEndDate],
  );

  // Date validation
  const validateDates = (): boolean => {
    const leadStart = moment(leadStartDate);
    const leadEnd = moment(leadEndDate);
    const revenueStart = moment(revenueStartDate);
    const revenueEnd = moment(revenueEndDate);

    if (
      leadStart.year() < 1900 ||
      leadStart.year() > 2100 ||
      leadEnd.year() < 1900 ||
      leadEnd.year() > 2100 ||
      revenueStart.year() < 1900 ||
      revenueStart.year() > 2100 ||
      revenueEnd.year() < 1900 ||
      revenueEnd.year() > 2100
    ) {
      setValidationError('Jahresangabe muss zwischen 1900 und 2100 liegen');
      return false;
    }

    if (leadStart.isAfter(leadEnd)) {
      setValidationError('Lead-Startdatum muss vor Lead-Enddatum liegen');
      return false;
    }

    if (revenueStart.isAfter(revenueEnd)) {
      setValidationError('Umsatz-Startdatum muss vor Umsatz-Enddatum liegen');
      return false;
    }

    setValidationError('');
    return true;
  };

  // Manual load data function
  const loadData = async () => {
    if (!validateDates()) {
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.allSettled([
        fetchVnrRevenueSummary(
          leadStartDateStr,
          leadEndDateStr,
          revenueStartDateStr,
          revenueEndDateStr,
          selectedSources,
        ),
        fetchVnrRevenueTimeline(
          leadStartDateStr,
          leadEndDateStr,
          revenueStartDateStr,
          revenueEndDateStr,
          selectedSources,
        ),
        fetchVnrSourceAnalysis(
          leadStartDateStr,
          leadEndDateStr,
          revenueStartDateStr,
          revenueEndDateStr,
          selectedSources,
        ),
        fetchVnrNewsletterAnalysis(
          leadStartDateStr,
          leadEndDateStr,
          revenueStartDateStr,
          revenueEndDateStr,
          selectedSources,
        ),
        fetchVnrDomainAnalysis(
          leadStartDateStr,
          leadEndDateStr,
          revenueStartDateStr,
          revenueEndDateStr,
          selectedSources,
        ),
      ]);

      if (results[0].status === 'fulfilled') setSummary(results[0].value);
      if (results[1].status === 'fulfilled') setTimeline(results[1].value);
      if (results[2].status === 'fulfilled') setSourceAnalysis(results[2].value);
      if (results[3].status === 'fulfilled') setNewsletterAnalysis(results[3].value);
      if (results[4].status === 'fulfilled') setDomainAnalysis(results[4].value);
    } catch (error) {
      console.error('Error loading VNR revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedSources(typeof value === 'string' ? value.split(',') : value);
  };

  // Chart data preparation
  const chartData = useMemo(() => {
    if (timeline.length === 0) return [];

    const maxSignups = Math.max(...timeline.map((item) => item.signups));
    const maxRevenue = Math.max(...timeline.map((item) => item.revenue));
    const revenueNormalizationFactor = (maxSignups / maxRevenue) * 0.5;

    return [
      {
        id: 'Neue E-Mail Anmeldungen',
        color: colors.greenAccent[500],
        data: timeline.map((item) => ({
          x: item.month,
          y: item.signups,
          originalValue: item.signups,
        })),
      },
      {
        id: 'Umsatz €',
        color: colors.blueAccent[500],
        data: timeline.map((item) => ({
          x: item.month,
          y: item.revenue * revenueNormalizationFactor,
          originalValue: item.revenue,
        })),
      },
    ];
  }, [timeline, colors]);

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="600" mb="20px">
        VNR-Umsätze
      </Typography>

      {/* Info Panel */}
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Kohorten-basierte Analyse:</strong> Lead-Datum filtert E-Mails, die sich ERSTMALIG
          in diesem Zeitraum als VNR-Leads angemeldet haben (Signup-Kohorte). Umsatz-Datum filtert,
          wann diese Kohorte Umsätze generiert hat. Dies ermöglicht die Analyse: &quot;Welche
          E-Mails meldeten sich erstmals an, und welchen Umsatz generierten sie später?&quot;
        </Typography>
      </Alert>

      {/* Filters */}
      <Box display="flex" flexWrap="wrap" gap="20px" mb="20px">
        <Box>
          <Typography variant="body2" mb="5px">
            Lead-Datum Start
          </Typography>
          <TextField
            type="date"
            value={moment(leadStartDate).format('YYYY-MM-DD')}
            onChange={(e) => setLeadStartDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>
        <Box>
          <Typography variant="body2" mb="5px">
            Lead-Datum Ende
          </Typography>
          <TextField
            type="date"
            value={moment(leadEndDate).format('YYYY-MM-DD')}
            onChange={(e) => setLeadEndDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>
        <Box>
          <Typography variant="body2" mb="5px">
            Umsatz-Datum Start
          </Typography>
          <TextField
            type="date"
            value={moment(revenueStartDate).format('YYYY-MM-DD')}
            onChange={(e) => setRevenueStartDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>
        <Box>
          <Typography variant="body2" mb="5px">
            Umsatz-Datum Ende
          </Typography>
          <TextField
            type="date"
            value={moment(revenueEndDate).format('YYYY-MM-DD')}
            onChange={(e) => setRevenueEndDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>
        <Box minWidth="200px">
          <Typography variant="body2" mb="5px">
            Quelle
          </Typography>
          <Select
            multiple
            value={selectedSources}
            onChange={handleSourceChange}
            size="small"
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value.toUpperCase()} size="small" />
                ))}
              </Box>
            )}
          >
            <MenuItem value="nl">NL</MenuItem>
            <MenuItem value="sam">SAM</MenuItem>
          </Select>
        </Box>
        <Box display="flex" alignItems="flex-end">
          <Button variant="contained" onClick={loadData} disabled={loading} sx={{ height: '40px' }}>
            {loading ? <CircularProgress size={24} /> : 'Daten laden'}
          </Button>
        </Box>
      </Box>

      {/* Validation Error */}
      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {/* Summary KPI Cards */}
      {loading && <CircularProgress />}
      {summary && (
        <>
          {/* Row 1: Signup Cohort Metrics (Green) */}
          <Typography variant="h6" fontWeight="600" mb="10px">
            Kohortenmetriken - Erstanmeldungen
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
            gap="20px"
            mb="20px"
          >
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap="5px">
                  <Typography variant="body2" color="textSecondary">
                    Gesamte E-Mails
                  </Typography>
                  <Tooltip
                    title={
                      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                        <strong>
                          Anzahl E-Mails, die sich ERSTMALIG im Lead-Zeitraum angemeldet haben
                        </strong>
                        <br />
                        Formel: COUNT(DISTINCT email WHERE MIN(vld.datum) BETWEEN leadStart AND
                        leadEnd)
                        <br />
                        Filtert nach: <strong>Lead-Datum</strong> (Datum der ersten
                        VNR-Lead-Anmeldung)
                        <br />
                        Beispiel: 10.350 E-Mails meldeten sich erstmals zwischen 2024-11-18 und
                        2025-11-18 an
                      </div>
                    }
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: colors.grey[400] }} />
                  </Tooltip>
                </Box>
                <Typography variant="h4" color={colors.greenAccent[400]}>
                  {formatNumber(summary.total_emails, 0)}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap="5px">
                  <Typography variant="body2" color="textSecondary">
                    E-Mails mit Umsatz
                  </Typography>
                  <Tooltip
                    title={
                      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                        <strong>E-Mails aus der Signup-Kohorte, die Umsatz generiert haben</strong>
                        <br />
                        Formel: COUNT(DISTINCT email WHERE email_revenue {'>'} 0)
                        <br />
                        Filtert: Kohorte nach <strong>Lead-Datum</strong>, Umsatz nach{' '}
                        <strong>Umsatz-Datum</strong>
                        <br />
                        Beispiel: 1.503 der 10.350 Erstanmeldungen generierten später Umsatz
                      </div>
                    }
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: colors.grey[400] }} />
                  </Tooltip>
                </Box>
                <Typography variant="h4" color={colors.greenAccent[400]}>
                  {formatNumber(summary.emails_with_revenue, 0)}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap="5px">
                  <Typography variant="body2" color="textSecondary">
                    Conversion-Rate
                  </Typography>
                  <Tooltip
                    title={
                      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                        <strong>Prozentsatz der Kohorte, die Umsatz generiert hat</strong>
                        <br />
                        Formel: (E-Mails mit Umsatz ÷ Gesamte E-Mails) × 100
                        <br />
                        Berechnung: (1.503 ÷ 10.350) × 100 = 14,52%
                        <br />
                        Bedeutung: Von 100 Erstanmeldungen generieren ~15 später Umsatz
                      </div>
                    }
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: colors.grey[400] }} />
                  </Tooltip>
                </Box>
                <Typography variant="h4" color={colors.greenAccent[400]}>
                  {formatNumber(summary.conversion_rate, 2)} %
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Row 2: Revenue Metrics (Blue) */}
          <Typography variant="h6" fontWeight="600" mb="10px">
            Umsatz-Metriken (Kohorten-Umsatz)
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
            gap="20px"
            mb="20px"
          >
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap="5px">
                  <Typography variant="body2" color="textSecondary">
                    Gesamtumsatz
                  </Typography>
                  <Tooltip
                    title={
                      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                        <strong>Umsatz, der von der Signup-Kohorte generiert wurde</strong>
                        <br />
                        Formel: SUM(va.costs) WHERE email IN signup_cohort
                        <br />
                        Filtert: Kohorte nach <strong>Lead-Datum</strong>, Umsatz nach{' '}
                        <strong>Umsatz-Datum</strong>
                        <br />
                        Beispiel: €132.785 Umsatz von den 10.350 Erstanmeldungen
                      </div>
                    }
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: colors.grey[400] }} />
                  </Tooltip>
                </Box>
                <Typography variant="h4" color={colors.blueAccent[400]}>
                  {formatNumber(summary.total_revenue, 2)} €
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap="5px">
                  <Typography variant="body2" color="textSecondary">
                    Ø Umsatz pro E-Mail
                  </Typography>
                  <Tooltip
                    title={
                      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                        <strong>
                          Durchschnittlicher Umsatz pro E-Mail der Kohorte (inkl. Nicht-Käufer)
                        </strong>
                        <br />
                        Formel: Gesamtumsatz ÷ Gesamte E-Mails
                        <br />
                        Berechnung: €132.785 ÷ 10.350 = €12,83
                        <br />
                        Bedeutung: Jede Erstanmeldung generiert durchschnittlich €12,83 Umsatz
                        <br />
                        Wichtig für ROI-Kalkulation von Lead-Generierungs-Kampagnen
                      </div>
                    }
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: colors.grey[400] }} />
                  </Tooltip>
                </Box>
                <Typography variant="h4" color={colors.blueAccent[400]}>
                  {formatNumber(summary.avg_revenue_per_email, 2)} €
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap="5px">
                  <Typography variant="body2" color="textSecondary">
                    Ø Umsatz pro Kunde
                  </Typography>
                  <Tooltip
                    title={
                      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                        <strong>Durchschnittlicher Umsatz pro umsatzgenerierender E-Mail</strong>
                        <br />
                        Formel: Gesamtumsatz ÷ E-Mails mit Umsatz
                        <br />
                        Bedeutung: bisher bezahlter Umsatz je zahlender E-Mail — keine Prognose
                        <br />
                        Höher als Ø pro E-Mail, da nur zahlende Kunden gezählt werden
                      </div>
                    }
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: colors.grey[400] }} />
                  </Tooltip>
                </Box>
                <Typography variant="h4" color={colors.blueAccent[400]}>
                  {formatNumber(summary.avg_revenue_per_customer, 2)} €
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      {/* Tabs */}
      <Box mb="20px">
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Zeitverlauf" />
          <Tab label="Quelle" />
          <Tab label="Newsletter" />
          <Tab label="E-Mail Domains" />
        </Tabs>
      </Box>

      {/* Tab 1: Timeline */}
      {activeTab === 0 && (
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" mb="20px">
                Monatlicher Verlauf
              </Typography>
              {chartData.length > 0 && (
                <Box height="400px">
                  <ResponsiveLine
                    data={chartData}
                    margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                    xScale={{ type: 'point' }}
                    yScale={{
                      type: 'linear',
                      min: 'auto',
                      max: 'auto',
                      stacked: false,
                      reverse: false,
                    }}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: 'Monat',
                      legendOffset: 45,
                      legendPosition: 'middle',
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'Wert',
                      legendOffset: -50,
                      legendPosition: 'middle',
                    }}
                    colors={(d) => d.color}
                    pointSize={10}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    useMesh={true}
                    legends={[
                      {
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 100,
                        translateY: 0,
                        itemsSpacing: 0,
                        itemDirection: 'left-to-right',
                        itemWidth: 80,
                        itemHeight: 20,
                        symbolSize: 12,
                        symbolShape: 'circle',
                      },
                    ]}
                    tooltip={(point) => (
                      <Box
                        sx={{
                          background: 'white',
                          padding: '9px 12px',
                          border: '1px solid #ccc',
                          color: 'black',
                        }}
                      >
                        <strong>{point.point.serieId}</strong>:{' '}
                        {formatNumber((point.point.data as any).originalValue, 2)}
                      </Box>
                    )}
                    theme={{
                      axis: {
                        domain: { line: { stroke: colors.grey[100] } },
                        legend: { text: { fill: colors.grey[100] } },
                        ticks: {
                          line: { stroke: colors.grey[100], strokeWidth: 1 },
                          text: { fill: colors.grey[100] },
                        },
                      },
                      legends: { text: { fill: colors.grey[100] } },
                      tooltip: { container: { color: colors.primary[500] } },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Timeline Table */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
                      <TableCell>Monat</TableCell>
                      <TableCell align="right">Neue E-Mail Anmeldungen</TableCell>
                      <TableCell align="right">Umsatz (€)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timeline.map((row) => (
                      <TableRow key={row.month}>
                        <TableCell>{row.month}</TableCell>
                        <TableCell align="right">{formatNumber(row.signups, 0)}</TableCell>
                        <TableCell align="right">{formatNumber(row.revenue, 2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tab 2: Source Analysis */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb="20px">
              Analyse nach Quelle
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
                    <TableCell>Quelle</TableCell>
                    <TableCell align="right">E-Mails</TableCell>
                    <TableCell align="right">E-Mails mit Umsatz</TableCell>
                    <TableCell align="right">Conversion-Rate (%)</TableCell>
                    <TableCell align="right">Gesamtumsatz (€)</TableCell>
                    <TableCell align="right">Ø Umsatz/E-Mail (€)</TableCell>
                    <TableCell align="right">Ø Umsatz/Kunde (€)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sourceAnalysis.map((row) => (
                    <TableRow key={row.source}>
                      <TableCell>{row.source.toUpperCase()}</TableCell>
                      <TableCell align="right">{formatNumber(row.emails, 0)}</TableCell>
                      <TableCell align="right">
                        {formatNumber(row.emails_with_revenue, 0)}
                      </TableCell>
                      <TableCell align="right">{formatNumber(row.conversion_rate, 2)}</TableCell>
                      <TableCell align="right">{formatNumber(row.total_revenue, 2)}</TableCell>
                      <TableCell align="right">
                        {formatNumber(row.avg_revenue_per_email, 2)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(row.avg_revenue_per_customer, 2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 3: Newsletter Analysis */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb="20px">
              Analyse nach Newsletter
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
                    <TableCell>Newsletter</TableCell>
                    <TableCell align="right">E-Mails</TableCell>
                    <TableCell align="right">E-Mails mit Umsatz</TableCell>
                    <TableCell align="right">Conversion-Rate (%)</TableCell>
                    <TableCell align="right">Gesamtumsatz (€)</TableCell>
                    <TableCell align="right">Ø Umsatz/E-Mail (€)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {newsletterAnalysis.map((row) => (
                    <TableRow key={row.newsletter}>
                      <TableCell>{row.newsletter}</TableCell>
                      <TableCell align="right">{formatNumber(row.emails, 0)}</TableCell>
                      <TableCell align="right">
                        {formatNumber(row.emails_with_revenue, 0)}
                      </TableCell>
                      <TableCell align="right">{formatNumber(row.conversion_rate, 2)}</TableCell>
                      <TableCell align="right">{formatNumber(row.total_revenue, 2)}</TableCell>
                      <TableCell align="right">
                        {formatNumber(row.avg_revenue_per_email, 2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 4: Domain Analysis */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb="20px">
              Analyse nach E-Mail Domain
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
                    <TableCell>Domain</TableCell>
                    <TableCell align="right">E-Mails</TableCell>
                    <TableCell align="right">E-Mails mit Umsatz</TableCell>
                    <TableCell align="right">Conversion-Rate (%)</TableCell>
                    <TableCell align="right">Gesamtumsatz (€)</TableCell>
                    <TableCell align="right">Ø Umsatz/E-Mail (€)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {domainAnalysis.map((row) => (
                    <TableRow key={row.email_domain}>
                      <TableCell>{row.email_domain}</TableCell>
                      <TableCell align="right">{formatNumber(row.emails, 0)}</TableCell>
                      <TableCell align="right">
                        {formatNumber(row.emails_with_revenue, 0)}
                      </TableCell>
                      <TableCell align="right">{formatNumber(row.conversion_rate, 2)}</TableCell>
                      <TableCell align="right">{formatNumber(row.total_revenue, 2)}</TableCell>
                      <TableCell align="right">
                        {formatNumber(row.avg_revenue_per_email, 2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default VnrRevenue;
